import React, { useEffect, useState } from 'react';
import { hrmsListEmployees, hrmsCreateEmployee, hrmsUpdateEmployee, hrmsAllLeaves, hrmsDecideLeave } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Check, X, UserPlus } from 'lucide-react';

const emptyEmp = () => ({ full_name: '', email: '', mobile: '', designation: '', department: '', location: '', joining_date: '', manager_email: '', ctc: '' });

export const EmployeesPage = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => setRows(await hrmsListEmployees().catch(() => []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        const { full_name, mobile, designation, department, location, manager_email, ctc, status } = editing;
        await hrmsUpdateEmployee(editing.id, { full_name, mobile, designation, department, location, manager_email, ctc, status });
        toast({ title: 'Updated' });
      } else {
        await hrmsCreateEmployee(editing);
        toast({ title: 'Employee onboarded', description: 'Welcome email sent with temporary login.' });
      }
      setEditing(null); load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold">Employees</h1>
          <p className="text-sm text-slate-500">Onboarded team members. Auto-generated employee IDs.</p>
        </div>
        <Button onClick={() => setEditing(emptyEmp())} className="bg-orange-500 hover:bg-orange-600" data-testid="employee-new"><UserPlus className="h-4 w-4 mr-1" /> New employee</Button>
      </div>

      <div className="mt-6 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">Employee ID</th>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Designation</th>
            <th className="text-left px-4 py-3">Department</th>
            <th className="text-left px-4 py-3">Joining</th>
            <th className="text-left px-4 py-3">Status</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No employees yet. Onboard the first one manually or from an accepted offer.</td></tr>}
            {rows.map((e) => e && (
              <tr key={e.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => setEditing({ ...e })}>
                <td className="px-4 py-3 font-mono text-xs">{e.employee_id}</td>
                <td className="px-4 py-3 font-medium">{e.full_name}</td>
                <td className="px-4 py-3">{e.email}</td>
                <td className="px-4 py-3">{e.designation}</td>
                <td className="px-4 py-3">{e.department || '—'}</td>
                <td className="px-4 py-3">{e.joining_date || '—'}</td>
                <td className="px-4 py-3"><Badge className={e.status === 'active' ? 'bg-emerald-500' : 'bg-slate-500'}>{e.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-xl">
          <DialogTitle>{editing?.id ? 'Edit employee' : 'Onboard employee'}</DialogTitle>
          {editing && (
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Full name *</Label><Input value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} data-testid="employee-name" /></div>
              <div><Label>Email *</Label><Input disabled={!!editing.id} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="employee-email" /></div>
              <div><Label>Mobile</Label><Input value={editing.mobile} onChange={(e) => setEditing({ ...editing, mobile: e.target.value })} /></div>
              <div><Label>Designation</Label><Input value={editing.designation} onChange={(e) => setEditing({ ...editing, designation: e.target.value })} data-testid="employee-designation" /></div>
              <div><Label>Department</Label><Input value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} /></div>
              <div><Label>Location</Label><Input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
              <div><Label>Joining date</Label><Input type="date" value={editing.joining_date} onChange={(e) => setEditing({ ...editing, joining_date: e.target.value })} /></div>
              <div><Label>Manager email</Label><Input value={editing.manager_email} onChange={(e) => setEditing({ ...editing, manager_email: e.target.value })} /></div>
              <div><Label>CTC</Label><Input value={editing.ctc} onChange={(e) => setEditing({ ...editing, ctc: e.target.value })} /></div>
              {editing.id && (
                <div className="col-span-2"><Label>Status</Label>
                  <select value={editing.status || 'active'} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
                    <option value="active">Active</option><option value="on_leave">On leave</option><option value="separated">Separated</option>
                  </select>
                </div>
              )}
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="employee-save">{saving ? 'Saving…' : 'Save'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const LeavesAdminPage = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const load = async () => setRows(await hrmsAllLeaves().catch(() => []));
  useEffect(() => { load(); }, []);

  const decide = async (l, status) => {
    const comment = status === 'rejected' ? (window.prompt('Reason for rejection?') || '') : '';
    await hrmsDecideLeave(l.id, status, comment);
    toast({ title: `Leave ${status}` });
    load();
  };

  return (
    <div data-testid="leaves-admin-page">
      <h1 className="text-2xl font-extrabold">Leave requests</h1>
      <p className="text-sm text-slate-500 mt-1">Approve or reject employee leave applications.</p>
      <div className="mt-6 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">Employee</th>
            <th className="text-left px-4 py-3">Type</th>
            <th className="text-left px-4 py-3">From</th>
            <th className="text-left px-4 py-3">To</th>
            <th className="text-left px-4 py-3">Reason</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No leave requests yet.</td></tr>}
            {rows.map((l) => (
              <tr key={l.id} className="border-t">
                <td className="px-4 py-3 font-medium">{l.employee_name}<div className="text-xs text-slate-500">{l.employee_id}</div></td>
                <td className="px-4 py-3">{l.leave_type}</td>
                <td className="px-4 py-3">{l.from_date}</td>
                <td className="px-4 py-3">{l.to_date}</td>
                <td className="px-4 py-3 max-w-xs whitespace-normal">{l.reason || '—'}</td>
                <td className="px-4 py-3"><Badge className={l.status === 'approved' ? 'bg-emerald-500' : l.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'}>{l.status}</Badge></td>
                <td className="px-4 py-3 space-x-1">
                  {l.status === 'pending' && (
                    <>
                      <Button onClick={() => decide(l, 'approved')} className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700"><Check className="h-3.5 w-3.5" /></Button>
                      <Button variant="outline" onClick={() => decide(l, 'rejected')} className="h-8 px-2 text-red-600 border-red-200"><X className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeesPage;
